<template>
    <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)">
        <v-card class="background-2 round-card">
            <v-card-title class="pa-3">
                <p class="pl-2 pl-xl-3">Assegna foto alle varianti</p>
                <v-spacer></v-spacer>
                <v-btn icon @click="close"><v-icon>close</v-icon></v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pt-4 panel-body">
                <v-progress-linear v-if="loading" height="2" class="my-0" indeterminate></v-progress-linear>

                <!-- PASSO FOTO-DELLA-VARIANTE: porta "cella variante". Multi-selezione:
                     una variante puo' mostrare PIU' foto (pivot n-n), quindi qui si
                     spuntano tutte le foto che devono reclamarla — si salva solo la
                     differenza, un replace-set per ogni foto cambiata. -->
                <div v-if="step === 'variantPhotos'">
                    <div class="body-2 grey--text mb-3">
                        Spunta le foto di <strong>{{ presetVariantLabel || 'questa variante' }}</strong>: si salva esattamente ciò che è spuntato. La prima (per ordine di caricamento) fa da foto principale sull'app.
                    </div>
                    <div v-if="!loading && !photos.length" class="body-2 grey--text">
                        Nessuna immagine nel File Storage del prodotto: caricala prima da lì.
                    </div>
                    <v-row v-else dense>
                        <v-col v-for="photo in photos" :key="photo.id" cols="6" sm="4" md="3">
                            <v-card
                                outlined
                                class="panel-photo-card"
                                :class="{'panel-photo-card--checked': isPhotoChecked(photo)}"
                                @click="togglePhoto(photo)"
                            >
                                <img v-if="previewSrc(photo)" :src="previewSrc(photo)" class="panel-photo-thumb" :alt="photoLabel(photo)">
                                <div v-else class="panel-photo-thumb panel-photo-thumb--empty">
                                    <v-icon color="grey lighten-1">image</v-icon>
                                </div>
                                <v-icon small class="panel-check-badge" :color="isPhotoChecked(photo) ? 'primary' : 'grey lighten-1'">
                                    {{ isPhotoChecked(photo) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                                </v-icon>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn icon x-small class="panel-cover-btn" v-on="on" :disabled="savingCover" @click.stop="electCover(photo)">
                                            <v-icon small :color="photo.is_cover ? 'amber darken-2' : 'grey'">{{ photo.is_cover ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ photo.is_cover ? 'Rimuovi la copertina' : 'Eleggi come copertina' }}</span>
                                </v-tooltip>
                                <div class="panel-photo-name" :title="photoLabel(photo)">{{ photoLabel(photo) }}</div>
                                <div class="panel-photo-state" :class="photoStateClass(photo)">{{ photoState(photo) }}</div>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- PASSO FOTO (scelta singola): porta generica senza foto ne' variante -->
                <div v-else-if="step === 'photos'">
                    <div class="body-2 grey--text mb-3">
                        Scegli la foto da assegnare.
                    </div>
                    <div v-if="!loading && !photos.length" class="body-2 grey--text">
                        Nessuna immagine nel File Storage del prodotto: caricala prima da lì.
                    </div>
                    <v-row v-else dense>
                        <v-col v-for="photo in photos" :key="photo.id" cols="6" sm="4" md="3">
                            <v-card outlined class="panel-photo-card" @click="pickPhoto(photo)">
                                <img v-if="previewSrc(photo)" :src="previewSrc(photo)" class="panel-photo-thumb" :alt="photoLabel(photo)">
                                <div v-else class="panel-photo-thumb panel-photo-thumb--empty">
                                    <v-icon color="grey lighten-1">image</v-icon>
                                </div>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn icon x-small class="panel-cover-btn" v-on="on" :disabled="savingCover" @click.stop="electCover(photo)">
                                            <v-icon small :color="photo.is_cover ? 'amber darken-2' : 'grey'">{{ photo.is_cover ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ photo.is_cover ? 'Rimuovi la copertina' : 'Eleggi come copertina' }}</span>
                                </v-tooltip>
                                <div class="panel-photo-name" :title="photoLabel(photo)">{{ photoLabel(photo) }}</div>
                                <div class="panel-photo-state" :class="photoStateClass(photo)">{{ photoState(photo) }}</div>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- PASSO VARIANTI: elenco con checkbox, le senza-foto evidenziate -->
                <div v-else>
                    <div v-if="selectedPhoto" class="d-flex align-center mb-4">
                        <img v-if="previewSrc(selectedPhoto)" :src="previewSrc(selectedPhoto)" class="panel-selected-thumb" :alt="photoLabel(selectedPhoto)">
                        <div v-else class="panel-selected-thumb panel-photo-thumb--empty">
                            <v-icon color="grey lighten-1">image</v-icon>
                        </div>
                        <div class="ml-3" style="min-width: 0;">
                            <div class="font-weight-medium text-truncate">
                                {{ photoLabel(selectedPhoto) }}
                                <v-chip v-if="selectedPhoto.is_cover" x-small color="amber darken-2" text-color="white" class="ml-1">Copertina</v-chip>
                            </div>
                            <div class="caption grey--text">
                                Spunta le varianti che devono mostrare questa foto: si salva esattamente ciò che è spuntato.
                            </div>
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn small text color="amber darken-2" :loading="savingCover" @click="electCover(selectedPhoto)">
                            <v-icon small left>{{ selectedPhoto.is_cover ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                            {{ selectedPhoto.is_cover ? 'Rimuovi copertina' : 'Eleggi copertina' }}
                        </v-btn>
                    </div>

                    <div v-if="!loading && !variants.length" class="body-2 grey--text">
                        Questo prodotto non ha varianti salvate: creale nella tab Varianti (e premi Aggiorna) prima di assegnare le foto.
                    </div>

                    <template v-else>
                        <div class="d-flex align-center mb-2">
                            <div class="caption grey--text">
                                <v-icon x-small color="warning">mdi-image-off-outline</v-icon>
                                = variante senza nessuna foto assegnata
                            </div>
                            <v-spacer></v-spacer>
                            <v-btn small text color="primary" @click="toggleAll">
                                {{ checked.length ? 'Smarca tutte' : 'Spunta tutte' }}
                            </v-btn>
                        </div>

                        <v-list dense class="panel-variant-list background-2">
                            <v-list-item
                                v-for="variant in variants"
                                :key="variant.id"
                                class="panel-variant-row"
                                :class="{'panel-variant-row--uncovered': !isCovered(variant.id)}"
                                @click="toggleVariant(variant.id)"
                            >
                                <v-list-item-action class="mr-3 my-0">
                                    <v-simple-checkbox
                                        color="primary"
                                        :value="checked.indexOf(variant.id) !== -1"
                                        @input="toggleVariant(variant.id)"
                                    ></v-simple-checkbox>
                                </v-list-item-action>
                                <v-list-item-content class="py-1">
                                    <v-list-item-title class="body-2">{{ variant.name || variant.code || ('Variante ' + variant.id) }}</v-list-item-title>
                                    <div v-if="variantSummaryParts(variant).length" class="panel-variant-attrs">
                                        <span v-for="part in variantSummaryParts(variant)" :key="part.name" class="mr-3">
                                            {{ part.name }}: <strong>{{ part.value }}</strong>
                                        </span>
                                    </div>
                                </v-list-item-content>
                                <v-list-item-icon v-if="!isCovered(variant.id)" class="my-auto">
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on" small color="warning">mdi-image-off-outline</v-icon>
                                        </template>
                                        <span>Nessuna foto assegnata a questa variante</span>
                                    </v-tooltip>
                                </v-list-item-icon>
                            </v-list-item>
                        </v-list>
                    </template>
                </div>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions>
                <v-btn v-if="step === 'variants' && !photoLocked" text :disabled="saving" @click="step = 'photos'">
                    Cambia foto
                </v-btn>
                <div v-if="step === 'variants' && variants.length" class="caption grey--text ml-2">
                    {{ checked.length }} spuntate su {{ variants.length }}
                </div>
                <div v-if="step === 'variantPhotos' && photos.length" class="caption grey--text ml-2">
                    {{ photoChecked.length }} foto spuntate su {{ photos.length }}
                </div>
                <v-spacer></v-spacer>
                <v-btn text color="info" :disabled="saving" @click="close">Annulla</v-btn>
                <v-btn
                    v-if="step === 'variants'"
                    text
                    color="primary"
                    :loading="saving"
                    :disabled="!selectedPhoto || !variants.length"
                    @click="save"
                >Salva</v-btn>
                <v-btn
                    v-if="step === 'variantPhotos'"
                    text
                    color="primary"
                    :loading="saving"
                    :disabled="!photos.length"
                    @click="saveVariantPhotos"
                >Salva</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import Vue from 'vue'
    import { isImageExt } from '@/components/utilities/imageFiles'
    import capacitorService from '@/services/capacitor-service'

    export default {
        name: 'photo-variant-assign-panel',

        props: {
            value: Boolean,
            productId: Number,
            // Porta File Storage: la foto e' nota, si scelgono le varianti.
            // Assente = porta cella varianti: prima si sceglie la foto.
            photo: Object,
            // Porta cella varianti: quella variante arriva gia' spuntata
            presetVariantId: Number
        },

        data() {
            return {
                step: 'variants',
                variants: [],
                photos: [],
                selectedPhoto: null,
                checked: [],
                photoChecked: [],
                attributesList: [],
                loading: false,
                saving: false,
                savingCover: false,
                previewUrls: []
            }
        },

        computed: {
            // Porta File Storage: la foto non si cambia da qui (si sceglie nel DMS)
            photoLocked() {
                return !!this.photo
            },

            /**
             * Varianti reclamate da ALMENO una foto: chi non c'e' e' "senza foto"
             * e va evidenziata — la domanda vera mentre si assegna e' "cosa mi manca".
             */
            coveredVariantIds() {
                const covered = {}
                this.photos.forEach((photo) => {
                    (photo.variant_ids || []).forEach((id) => { covered[id] = true })
                })
                return covered
            },

            presetVariantLabel() {
                if (!this.presetVariantId) return null
                const variant = this.variants.find((v) => v.id === this.presetVariantId)
                return variant ? (variant.name || variant.code || ('Variante ' + variant.id)) : null
            }
        },

        watch: {
            value(opened) {
                if (opened) this.bootstrap()
            }
        },

        methods: {
            bootstrap() {
                // Tre porte: foto nota (File Storage) → spunta varianti; variante nota
                // (cella tabella) → spunta le SUE foto (n-n: anche piu' d'una); nessuna
                // delle due → scelta foto singola.
                this.step = this.photo ? 'variants' : (this.presetVariantId ? 'variantPhotos' : 'photos')
                this.selectedPhoto = null
                this.checked = []
                this.photoChecked = []
                this.loadAll()
            },

            loadAll() {
                if (!this.productId) return
                this.loading = true
                const variantsCall = this.axios.get('products/' + this.productId + '/variants/edit')
                const attributesCall = this.axios.get('products/' + this.productId + '/attributes')
                const photosCall = this.axios.post('attachmentslists/', { attachable_type: 'product', id: this.productId })

                Promise.all([variantsCall, attributesCall, photosCall]).then(([variantsRes, attributesRes, photosRes]) => {
                    // Solo varianti con id REALE: le righe locali non salvate della tab
                    // Varianti non arrivano da questo endpoint, e non devono comunque
                    // ricevere assegnazioni (id fantasma)
                    this.variants = (variantsRes.data.model || []).filter((v) => v.id > 0)
                    this.attributesList = attributesRes.data.attributes || []
                    const all = Array.isArray(photosRes.data.attachments) ? photosRes.data.attachments : []
                    this.photos = all.filter((a) => isImageExt(a.ext)).sort((a, b) => a.id - b.id)
                    this.loadMissingThumbnails()

                    if (this.photo) {
                        // La versione fresca (con variant_ids) vince sull'oggetto passato
                        this.selectPhoto(this.photos.find((p) => p.id === this.photo.id) || this.photo)
                    }
                    if (this.step === 'variantPhotos') {
                        this.photoChecked = this.photos
                            .filter((p) => (p.variant_ids || []).indexOf(this.presetVariantId) !== -1)
                            .map((p) => p.id)
                    }
                    this.loading = false
                }, () => {
                    this.loading = false
                    this.$store.commit('setSnackbarError')
                })
            },

            loadMissingThumbnails() {
                if (capacitorService.isNative) return
                this.photos.forEach((photo) => {
                    if (photo.thumbnail || !photo.path || photo._previewUrl) return
                    this.$eventBusUtility.getFile(this.$store, '/files/' + photo.path, photo.name, true)
                        .then((url) => {
                            if (!url) return
                            this.previewUrls.push(url)
                            Vue.set(photo, '_previewUrl', url)
                        })
                        .catch(() => {})
                })
            },

            selectPhoto(photo) {
                this.selectedPhoto = photo
                const ids = (photo.variant_ids || []).slice()
                if (this.presetVariantId && ids.indexOf(this.presetVariantId) === -1) {
                    ids.push(this.presetVariantId)
                }
                this.checked = ids
            },

            pickPhoto(photo) {
                this.selectPhoto(photo)
                this.step = 'variants'
            },

            toggleVariant(id) {
                const index = this.checked.indexOf(id)
                if (index === -1) this.checked.push(id)
                else this.checked.splice(index, 1)
            },

            isPhotoChecked(photo) {
                return this.photoChecked.indexOf(photo.id) !== -1
            },

            togglePhoto(photo) {
                const index = this.photoChecked.indexOf(photo.id)
                if (index === -1) this.photoChecked.push(photo.id)
                else this.photoChecked.splice(index, 1)
            },

            toggleAll() {
                this.checked = this.checked.length ? [] : this.variants.map((v) => v.id)
            },

            isCovered(variantId) {
                return !!this.coveredVariantIds[variantId]
            },

            /**
             * Input:  variante di variants/edit, id attributo
             * Output: testo del valore, o null — `values` e' `[]` quando manca,
             *         e in JS `[]` e' truthy, da cui il controllo su Array
             */
            variantValueOf(variant, attributeId) {
                const entry = variant.attributes ? variant.attributes[attributeId] : null
                if (!entry || !entry.values || Array.isArray(entry.values)) return null
                const text = entry.values.text
                return text == null || text === '' ? null : text
            },

            // Parti strutturate "colore: viola" per la riga variante: il valore va
            // in <strong> e a colore pieno — la stringa unica grigia non si leggeva.
            variantSummaryParts(variant) {
                return this.attributesList
                    .map((attribute) => {
                        const text = this.variantValueOf(variant, attribute.id)
                        return text == null ? null : { name: attribute.name, value: text }
                    })
                    .filter((part) => part !== null)
            },

            photoLabel(photo) {
                if (!photo) return ''
                if (photo.ext && !String(photo.name).endsWith('.' + photo.ext)) {
                    return photo.name + '.' + photo.ext
                }
                return photo.name
            },

            previewSrc(photo) {
                if (!photo) return null
                if (photo.thumbnail) return 'data:image/jpeg;base64,' + photo.thumbnail
                return photo._previewUrl || null
            },

            photoState(photo) {
                const count = (photo.variant_ids || []).length
                if (photo.is_cover) {
                    return count ? 'Copertina · ' + count + ' varianti' : 'Copertina'
                }
                if (!count) return 'Nessuna variante'
                return count === 1 ? '1 variante' : count + ' varianti'
            },

            photoStateClass(photo) {
                if (photo.is_cover) return 'amber--text text--darken-2'
                return (photo.variant_ids || []).length ? 'primary--text' : 'grey--text'
            },

            /**
             * Input:  foto della griglia (o la selezionata)
             * Fa:     elegge la copertina del prodotto — o la toglie se e' gia' lei.
             *         La copertina e' UNA: eleggerne una spegne il flag sulle altre.
             * Output: nessuno (aggiorna lo stato locale + avvisa gli altri consumer)
             */
            electCover(photo) {
                if (!photo) return
                const makeCover = !photo.is_cover
                this.savingCover = true

                this.axios.post('attachments/cover', {
                    product_id: this.productId,
                    attachment_id: makeCover ? photo.id : null
                }).then(() => {
                    this.savingCover = false
                    this.photos.forEach((p) => Vue.set(p, 'is_cover', makeCover && p.id === photo.id))
                    if (this.selectedPhoto && this.photos.indexOf(this.selectedPhoto) === -1) {
                        Vue.set(this.selectedPhoto, 'is_cover', makeCover && this.selectedPhoto.id === photo.id)
                    }
                    this.$emit('cover-changed', { attachment_id: makeCover ? photo.id : null })
                    // Il File Storage (DmsUploader) etichetta le foto in base a
                    // copertina+legami: va avvisato anche se non e' un genitore
                    this.$eventBusUtility.$emit('variantPhotoAssignmentsChanged')
                    this.$store.commit('setSnackbar', {
                        timeout: 3000,
                        text: makeCover ? 'Copertina impostata' : 'Copertina rimossa',
                        value: true,
                        color: 'success'
                    })
                }, () => {
                    this.savingCover = false
                    this.$store.commit('setSnackbar', {
                        timeout: 6000, text: 'Errore nell\'impostazione della copertina', value: true, color: 'error'
                    })
                })
            },

            save() {
                if (!this.selectedPhoto) return
                this.saving = true
                const ids = this.checked.slice()

                this.axios.post('attachments/variants', {
                    attachment_id: this.selectedPhoto.id,
                    variant_ids: ids
                }).then(() => {
                    this.saving = false
                    Vue.set(this.selectedPhoto, 'variant_ids', ids)
                    this.$emit('saved', { photo_id: this.selectedPhoto.id, variant_ids: ids })
                    this.$eventBusUtility.$emit('variantPhotoAssignmentsChanged')
                    this.$store.commit('setSnackbar', {
                        timeout: 3000,
                        text: ids.length
                            ? 'Foto assegnata a ' + ids.length + (ids.length === 1 ? ' variante' : ' varianti')
                            : 'Assegnazioni rimosse: la foto resta solo nel File Storage',
                        value: true,
                        color: 'success'
                    })
                    this.close()
                }, () => {
                    this.saving = false
                    this.$store.commit('setSnackbar', {
                        timeout: 6000, text: 'Errore nel salvataggio dell\'assegnazione', value: true, color: 'error'
                    })
                })
            },

            /**
             * Input:  photoChecked (foto spuntate per la variante preset)
             * Fa:     salva la DIFFERENZA — per ogni foto la cui appartenenza è
             *         cambiata, replace-set del suo variant_ids ± la variante.
             *         Le foto non toccate non generano richieste.
             * Output: nessuno (aggiorna lo stato locale + avvisa gli altri consumer)
             */
            saveVariantPhotos() {
                const variantId = this.presetVariantId
                if (!variantId) return

                const changes = this.photos
                    .map((photo) => {
                        const has = (photo.variant_ids || []).indexOf(variantId) !== -1
                        const want = this.photoChecked.indexOf(photo.id) !== -1
                        if (has === want) return null
                        const ids = (photo.variant_ids || []).slice()
                        if (want) ids.push(variantId)
                        else ids.splice(ids.indexOf(variantId), 1)
                        return { photo, ids }
                    })
                    .filter((c) => c !== null)

                if (!changes.length) {
                    this.close()
                    return
                }

                this.saving = true
                Promise.all(changes.map(({ photo, ids }) =>
                    this.axios.post('attachments/variants', {
                        attachment_id: photo.id,
                        variant_ids: ids
                    }).then(() => {
                        Vue.set(photo, 'variant_ids', ids)
                    })
                )).then(() => {
                    this.saving = false
                    this.$emit('saved', { variant_id: variantId, photo_ids: this.photoChecked.slice() })
                    this.$eventBusUtility.$emit('variantPhotoAssignmentsChanged')
                    const count = this.photoChecked.length
                    this.$store.commit('setSnackbar', {
                        timeout: 3000,
                        text: count
                            ? (count === 1 ? '1 foto assegnata' : count + ' foto assegnate') + ' alla variante'
                            : 'Nessuna foto assegnata a questa variante',
                        value: true,
                        color: 'success'
                    })
                    this.close()
                }, () => {
                    // Promise.all: alcune richieste possono essere passate (variant_ids
                    // locale aggiornato solo per quelle) — riaprire il pannello mostra
                    // lo stato reale dal server.
                    this.saving = false
                    // L'evento va emesso anche qui: le scritture andate a buon fine sono
                    // gia' sul server, e senza avviso File Storage e tab Varianti
                    // resterebbero a mostrare lo stato precedente proprio per quelle.
                    this.$eventBusUtility.$emit('variantPhotoAssignmentsChanged')
                    this.$store.commit('setSnackbar', {
                        timeout: 6000, text: 'Errore nel salvataggio di alcune assegnazioni: riapri il pannello per lo stato reale', value: true, color: 'error'
                    })
                })
            },

            close() {
                this.$emit('input', false)
            }
        },

        beforeDestroy() {
            this.previewUrls.forEach((url) => {
                try { URL.revokeObjectURL(url) } catch (e) { /* gia' revocato */ }
            })
            this.previewUrls = []
        }
    }
</script>

<style scoped>
    .panel-body {
        max-height: 70vh;
    }
    .panel-photo-card {
        position: relative;
        padding: 6px;
        cursor: pointer;
    }
    .panel-photo-card--checked {
        border-color: var(--v-primary-base, #1976d2);
        box-shadow: 0 0 0 1px var(--v-primary-base, #1976d2);
    }
    .panel-check-badge {
        position: absolute;
        top: 10px;
        left: 10px;
        background: rgba(255, 255, 255, 0.85);
        border-radius: 3px;
    }
    .panel-cover-btn {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(255, 255, 255, 0.85);
    }
    .panel-photo-thumb {
        width: 100%;
        height: 96px;
        object-fit: cover;
        border-radius: 4px;
        background: #f3f4f6;
        display: block;
    }
    .panel-photo-thumb--empty {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .panel-photo-name {
        font-size: 13px;
        margin-top: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .panel-photo-state {
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .panel-selected-thumb {
        width: 64px;
        height: 64px;
        flex: 0 0 64px;
        object-fit: cover;
        border-radius: 6px;
        background: #f3f4f6;
    }
    .panel-variant-list {
        max-height: 380px;
        overflow-y: auto;
        border: 1px solid rgba(128, 128, 128, 0.25);
        border-radius: 6px;
    }
    .panel-variant-row {
        min-height: 40px;
    }
    /* Attributi della variante ("colore: viola"): colore pieno, valore in bold —
       il sottotitolo grigio unico non si leggeva. */
    .panel-variant-attrs {
        font-size: 13px;
        line-height: 1.3;
        white-space: normal;
    }
    .panel-variant-row--uncovered {
        background: rgba(255, 152, 0, 0.08);
    }
</style>
